// Copyright (c) Bentley Systems
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import {
  getUrlBasedOnEnv,
  setErrorState,
} from "./helper-functions";

/**
 * Options for the useNodePermission hook.
 */
export type UseNodePermissionOptions = {
  /**
   * Additional headers to include in the permission check request.
   *
   * The most common use is passing `{ "Itwin-ID": iTwinId }` so the DawApi
   * backend (`BaseController.GetNode`) can resolve project-level RBAC
   * permissions. Without it the backend falls back to organisation-level
   * checks only.
   *
   * **Why a header instead of a query/route param?**
   * The permission endpoint `GET /api/Structure/Node/{uniqueName}` only
   * accepts a node name in the route — no iTwinId route segment, no query
   * param, and no request body (it is a GET). The `Itwin-ID` custom header
   * was added as the sole side-channel to pass the iTwin context for
   * permission evaluation, rather than changing the endpoint's REST
   * contract. This is a known design quirk, not an oversight.
   *
   * **When to pass it:**
   * Pass `Itwin-ID` when the component is opened in an iTwin-scoped context
   * where the iTwinId is already known (e.g. read from a `?iTwinId=` query
   * parameter in the page URL). Components that select the iTwin *after*
   * mount (e.g. via a ComboBox) typically do not need it for the initial
   * permission check — the backend will fall back to org-level auth.
   */
  extraHeaders?: Record<string, string>;
  /** When false, the permission check is skipped entirely and `isNodeAllowed` remains false. Defaults to true. */
  enabled?: boolean;
  /**
   * When true, a non-OK response is silently treated as "not allowed" instead of dispatching
   * an error state. Use this for optional sub-action permission checks (e.g. a toggle button
   * guarded by a stricter permission) where a 403 means the feature is restricted for this
   * user, not that the user landed on a page they shouldn't be on.
   * Defaults to false.
   */
  silent?: boolean;
}

/**
 * Hook that checks whether the current user has access to a specific DAW component node.
 * Encapsulates the repeated `/api/Structure/Node/{componentId}` permission check pattern.
 *
 * @param componentId - The DawComponent constant identifying the node to check.
 * @param options - Optional configuration for extra headers and conditional execution.
 * @returns `{ isNodeAllowed, isLoading }` — whether access is granted and loading state.
 */
export const useNodePermission = (
  componentId: string,
  options?: UseNodePermissionOptions,
) => {
  const enabled = options?.enabled ?? true;
  const silent = options?.silent ?? false;
  const extraHeaders = options?.extraHeaders;
  // Stabilise extraHeaders so inline objects don't trigger infinite re-renders
  const extraHeadersKey = JSON.stringify(extraHeaders);
  const stableExtraHeaders = useMemo(
    () => extraHeaders,
    [extraHeadersKey],
  );

  const [isNodeAllowed, setIsNodeAllowed] = useState(false);
  const [isLoading, setIsLoading] = useState(enabled);
  const accessToken = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const url = getUrlBasedOnEnv(`/api/Structure/Node/${componentId}`);
    const runId = crypto.randomUUID();

    setIsLoading(true);
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: runId,
        ...stableExtraHeaders,
      },
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          if (!silent) {
            setErrorState(response.status, dispatch, response.headers.get("X-Run-Id") ?? runId);
          }
          return;
        }
        setIsNodeAllowed(true);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      })
      .finally(() => setIsLoading(false));

    return () => {
      abortController.abort();
    };
  }, [accessToken, dispatch, componentId, enabled, silent, stableExtraHeaders]);

  return { isNodeAllowed, isLoading };
};
